/**
 * `/verify-email/:token` view — issue #117.
 *
 * Consumes the server-side `POST /auth/confirm-email` endpoint with the
 * token from the URL. The view runs the call on mount via a small
 * `setTimeout` (the router renders the node first; we then dispatch the
 * fetch — this gives a11y users the "Confirming your email…" announcement
 * before the result arrives).
 *
 * Outcomes:
 *   - 200 → success panel + link to /sign-in
 *   - 400 → token expired / invalid; offer a path back to sign-up
 */
import { div, h1, main, p, span, text, type Mountable, type Send, type Signal } from '@llui/dom';

import { t } from '../i18n.js';

import { confirmEmail } from './api.js';

interface VerifyState {
  token: string;
  status: 'pending' | 'success' | 'error';
}

const stateByToken = new Map<string, VerifyState['status']>();

function runConfirm(token: string, rootSelector: string): void {
  if (stateByToken.get(token) === 'success' || stateByToken.get(token) === 'error') return;
  stateByToken.set(token, 'pending');
  void (async (): Promise<void> => {
    const result = await confirmEmail(token);
    stateByToken.set(token, result.ok ? 'success' : 'error');
    // Replace the DOM in-place — the route hasn't changed, so we re-paint
    // by mutating the existing card content (avoids hooking the TEA loop
    // for a one-shot async result).
    const cardEl = document.querySelector<HTMLElement>(rootSelector);
    if (!cardEl) return;
    cardEl.innerHTML = '';
    cardEl.classList.add('auth-card--result');

    const head = document.createElement('div');
    head.className = 'auth-card-head';
    const glyph = document.createElement('span');
    glyph.className = `auth-result-glyph auth-result-glyph--${result.ok ? 'success' : 'error'}`;
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = result.ok ? '✓' : '!';
    head.appendChild(glyph);
    const heading = document.createElement('h1');
    heading.textContent = t('auth.verify.heading');
    head.appendChild(heading);
    cardEl.appendChild(head);

    const body = document.createElement('p');
    body.className = 'auth-card-subtitle';
    body.setAttribute('data-testid', result.ok ? 'verify-email-success' : 'verify-email-error');
    body.textContent = result.ok ? t('auth.verify.success') : t('auth.verify.error');
    cardEl.appendChild(body);

    const linkRow = document.createElement('div');
    linkRow.className = 'auth-form-links auth-form-links--center';
    const link = document.createElement('a');
    link.href = result.ok ? '/sign-in' : '/sign-up';
    link.setAttribute(
      'data-testid',
      result.ok ? 'verify-email-continue' : 'verify-email-request-new',
    );
    link.textContent = result.ok ? t('auth.verify.continue_link') : t('auth.verify.request_new_link');
    linkRow.appendChild(link);
    cardEl.appendChild(linkRow);
  })();
}

export function viewVerifyEmail<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
  token: string,
): Mountable {
  const rootSelector = `[data-verify-token="${CSS.escape(token)}"]`;
  // Kick off the confirm fetch on the next microtask so the DOM is
  // mounted before we try to find the card element.
  if (typeof window !== 'undefined') {
    queueMicrotask(() => runConfirm(token, rootSelector));
  }
  // The card root carries `data-verify-token` so the imperative
  // `runConfirm` can find and re-paint it after the async confirm. The
  // `card` atom doesn't forward arbitrary data-attributes, so the verify
  // card is a plain div wearing the same `.dl-card`/`.auth-card` chrome
  // classes (it's mutated imperatively anyway, never re-rendered by llui).
  return main({ role: 'main', class: 'auth-shell', 'data-testid': 'verify-email-view' }, [
    div(
      {
        class: 'dl-card dl-card--default dl-card--p-xl auth-card auth-card--result',
        'data-verify-token': token,
      },
      [
        div({ class: 'auth-card-head' }, [
          span({ class: 'auth-result-glyph auth-result-glyph--pending', 'aria-hidden': 'true' }, [
            text('…'),
          ]),
          h1([text(t('auth.verify.heading'))]),
        ]),
        p(
          {
            class: 'auth-card-subtitle',
            'data-testid': 'verify-email-pending',
            role: 'status',
            'aria-live': 'polite',
          },
          [text(t('auth.verify.pending'))],
        ),
      ],
    ),
  ]);
}
